import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Box, Button, CircularProgress, Snackbar, Tooltip } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { requestAuditReport, resetAuditReport } from "../../../store/auditHub/auditHubSlice";
import { AUDIT_STATUSES } from "../AuditHub/constants";
import { AH_WHITE } from "../AuditHub/theme";

const UNAVAILABLE_TIP = "A report is available after the assessment is completed.";

const buttonSx = {
  height: 36,
  minHeight: 36,
  px: 1.75,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8125rem",
  whiteSpace: "nowrap",
  boxShadow: "none",
  backgroundColor: "#1976d2",
  color: AH_WHITE,
  "&:hover": { backgroundColor: "#1565c0", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#90caf9", color: AH_WHITE },
};

/** Same report flow as the Audit Hub grid action: request, wait for the job, then open the download link. */
const AuditReportButton = ({ auditId, auditCode, status }) => {
  const dispatch = useDispatch();
  const report = useSelector((state) => state.auditHub?.report) || {};
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const ownReport = Boolean(auditId) && report.auditId === auditId;
  const generating = ownReport && report.status === "loading";
  const available = status === AUDIT_STATUSES.COMPLETED;

  useEffect(() => {
    if (!ownReport) return;
    if (report.status === "succeeded" && report.downloadUrl) {
      window.open(report.downloadUrl, "_blank", "noopener,noreferrer");
      setSnackbar({ open: true, message: "Report is ready.", severity: "success" });
      dispatch(resetAuditReport());
    } else if (report.status === "failed" && report.error) {
      setSnackbar({ open: true, message: report.error, severity: "error" });
      dispatch(resetAuditReport());
    }
  }, [ownReport, report.status, report.downloadUrl, report.error, dispatch]);

  const download = () => {
    if (!auditId || generating) return;
    dispatch(requestAuditReport({ auditId, format: "docx" }));
    setSnackbar({ open: true, message: `Generating report for ${auditCode || "this assessment"}…`, severity: "info" });
  };

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <>
      <Tooltip title={available ? "" : UNAVAILABLE_TIP}>
        <Box component="span" sx={{ display: "inline-flex", cursor: available ? "pointer" : "not-allowed" }}>
          <Button
            variant="contained"
            disableElevation
            onClick={download}
            disabled={!auditId || !available || generating}
            startIcon={generating ? <CircularProgress size={14} sx={{ color: AH_WHITE }} /> : <DownloadOutlinedIcon />}
            sx={{
              ...buttonSx,
              ...(!available
                ? { "&.Mui-disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8" } }
                : null),
            }}
          >
            {generating ? "Generating…" : "Download Report"}
          </Button>
        </Box>
      </Tooltip>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AuditReportButton;
